import React, { useState, useEffect, useRef } from 'react';
import { VERB_DICTIONARY } from '../constants/verbDictionary';

// ─── Verb pools ───────────────────────────────────────────────────────────────
const BEGINNER_VERBS = [
  "Essere", "Avere", "Fare", "Andare", "Venire", "Stare", "Volere", "Potere", "Nuotare", "Passare", "Ricordare", "Suonare",
  "Dovere", "Sapere", "Dare", "Dire", "Vedere", "Sentire", "Mangiare", "Bere", "Imparare", "Regalare", "Sciare",
  "Dormire", "Parlare", "Capire", "Studiare", "Lavorare", "Uscire", "Arrivare", "Insegnare", "Portare", "Salutare",
  "Partire", "Comprare", "Scrivere", "Leggere", "Ascoltare", "Guardare", "Guidare", "Raccontare",
  "Chiamare", "Amare", "Pensare", "Trovare", "Tornare", "Cucinare", "Abitare", "Incontrare", "Praticare", "Telefonare",
  "Aspettare", "Ballare", "Cambiare", "Cercare", "Cantare", "Frequentare", "Giocare", "Guadagnare", "Ripassare",
];

const INTERMEDIATE_ADDITIONS = [
  "Dimenticare", "Pagare", "Spiegare", "Viaggiare",
  "Scegliere", "Raccogliere", "Spegnere", "Spingere", "Vincere",
  "Muovere", "Promettere", "Offrire", "Aprire", "Soffrire", "Accendere", "Chiudere", "Perdere", "Mettere", "Prendere",
  "Rispondere", "Chiedere", "Correre", "Cadere", "Ridere", "Sorridere", "Piangere", "Preoccuparsi", "Divertirsi", "Svegliarsi",
  "Alzarsi", "Vestirsi", "Lavarsi", "Sedersi", "Riposarsi", "Curarsi", "Costruire", "Tradurre", "Aggiungere", "Decidere",
  "Succedere", "Esistere", "Nascere", "Crescere", "Vivere",
];

const INTERMEDIATE_FULL_LIST = [...BEGINNER_VERBS, ...INTERMEDIATE_ADDITIONS];

const ESSERE_VERBS = [
  'Andare', 'Apparire', 'Arrivare', 'Cadere', 'Costare', 'Diventare', 'Durare',
  'Entrare', 'Essere', 'Mancare', 'Morire', 'Nascere', 'Partire', 'Piacere',
  'Restare', 'Rimanere', 'Ritornare', 'Riuscire', 'Salire', 'Scendere',
  'Scomparire', 'Sbocciare', 'Stare', 'Succedere', 'Svenire', 'Tornare', 'Uscire', 'Venire',
];

const AUXILIARY_AMBIGUOUS = [
  'Cambiare', 'Cominciare', 'Correre', 'Finire', 'Girare', 'Incominciare',
  'Iniziare', 'Migliorare', 'Passare', 'Nevicare', 'Piovere',
  'Dovere', 'Potere', 'Volere', 'Volare','Vivere', 'Saltare'
];
const CORRECT_MESSAGES = ['Ottimo! ✨', 'Perfetto! 🎉', 'Wow! 👏', 'Esatto! ⭐', 'Eccellente! 🌟', 'Fantastico! 💫'];
const STREAK_MILESTONES = {
  3:  '🔥🔥 3 in a row — you\'re on fire!',
  5: '🔥🔥🔥 5 in a row — incredible!',
  8: '🔥🔥🔥🔥 8 in a row — unstoppable!',
};

const NOTE_TEXTS = {
  reflexive: 'Reflexive verbs (svegliarsi, vestirsi, divertirsi...) always use essere! No exceptions.',
  modal: 'With dovere, potere, volere + infinitive, the auxiliary depends on the verb that follows, not on dovere/potere/volere itself. (Shown here as the invariable form dovuto/potuto/voluto.)',
  modalReflexive: 'When the reflexive pronoun (mi, ti, si, ci, vi) sits before the modal verb, essere takes over as the auxiliary.',
  modalReflexiveAttached: 'When the reflexive pronoun is attached to the end of the infinitive instead of placed before the modal, the auxiliary goes back to avere.',
  transitive: 'This verb has a direct object here, so it takes avere.',
  intransitive: 'This verb has no direct object here, so it takes essere.',
};

const SENTENCE_EXERCISES = [
  // ═══════════════ BEGINNER (25) ═══════════════
  { sentence: 'Maria ___ mangiato il gelato.', correct: 'ha', difficulty: 'beginner' },
  { sentence: 'Giorgio ___ bevuto un caffè.', correct: 'ha', difficulty: 'beginner' },
  { sentence: 'Marta ___ andata dal dottore.', correct: 'è', difficulty: 'beginner' },
  { sentence: 'Gianluca ___ parlato con la sua fidanzata.', correct: 'ha', difficulty: 'beginner' },
  { sentence: 'Mia moglie ___ letto un libro interessante.', correct: 'ha', difficulty: 'beginner' },
  { sentence: 'Giulia ___ dormito sino alle otto.', correct: 'ha', difficulty: 'beginner' },
  { sentence: 'Marco e Luca ___ partiti per Roma.', correct: 'sono', options: ['sono', 'hanno'], difficulty: 'beginner' },
  { sentence: 'Il professore ___ spiegato la lezione.', correct: 'ha', difficulty: 'beginner' },
  { sentence: 'Sara ___ arrivata tardi alla festa.', correct: 'è', difficulty: 'beginner' },
  { sentence: 'I miei amici ___ usciti tardi ieri sera.', correct: 'sono', options: ['sono', 'hanno'], difficulty: 'beginner' },
  { sentence: 'Anna ___ nata a Milano.', correct: 'è', difficulty: 'beginner' },
  { sentence: 'Noi ___ ascoltato la radio in macchina.', correct: 'abbiamo', options: ['abbiamo', 'siamo'], difficulty: 'beginner' },
  { sentence: 'Loro ___ tornati a casa presto.', correct: 'sono', options: ['sono', 'hanno'], difficulty: 'beginner' },
  { sentence: 'Io ___ scritto una lettera.', correct: 'ho', options: ['ho', 'sono'], difficulty: 'beginner' },
  { sentence: 'Tu ___ uscita con le amiche ieri?', correct: 'sei', options: ['sei', 'hai'], difficulty: 'beginner' },
  { sentence: 'Tu ___ comprato un regalo per lei?', correct: 'hai', options: ['hai', 'sei'], difficulty: 'beginner' },
  { sentence: "Voi ___ studiato per l'esame?", correct: 'avete', options: ['avete', 'siete'], difficulty: 'beginner' },
  { sentence: 'Le ragazze ___ visto un film insieme.', correct: 'hanno', options: ['hanno', 'sono'], difficulty: 'beginner' },
  { sentence: 'I bambini ___ giocato a calcio.', correct: 'hanno', options: ['hanno', 'sono'], difficulty: 'beginner' },
  { sentence: "Mia sorella ___ tornata dall'università.", correct: 'è', difficulty: 'beginner' },
  { sentence: "Mio nonno ___ lavorato per quarant'anni.", correct: 'ha', difficulty: 'beginner' },
  { sentence: 'Il treno ___ partito in orario.', correct: 'è', difficulty: 'beginner' },
  { sentence: 'Le mie amiche ___ cucinato la cena.', correct: 'hanno', options: ['hanno', 'sono'], difficulty: 'beginner' },
  { sentence: 'Marco ___ salutato tutti prima di uscire.', correct: 'ha', difficulty: 'beginner' },
  { sentence: 'I miei genitori ___ venuti a trovarmi.', correct: 'sono', options: ['sono', 'hanno'], difficulty: 'beginner' },

  // ═══════════════ INTERMEDIATE (25) ═══════════════
  // reflexive verbs — always essere
  { sentence: 'Mi ___ svegliato alle sette.', correct: 'sono', options: ['sono', 'ho'], difficulty: 'intermediate', type: 'reflexive' },
  { sentence: 'Si ___ vestita in fretta.', correct: 'è', difficulty: 'intermediate', type: 'reflexive' },
  { sentence: 'Ci ___ divertiti moltissimo alla festa.', correct: 'siamo', options: ['siamo', 'abbiamo'], difficulty: 'intermediate', type: 'reflexive' },
  { sentence: 'Ti ___ lavata le mani?', correct: 'sei', options: ['sei', 'hai'], difficulty: 'intermediate', type: 'reflexive' },
  { sentence: 'Marco si ___ addormentato sul divano.', correct: 'è', options: ['è', 'ha'], difficulty: 'intermediate', type: 'reflexive' },
  { sentence: "Loro si ___ preoccupati per l'esame.", correct: 'sono', options: ['sono', 'hanno'], difficulty: 'intermediate', type: 'reflexive' },
  { sentence: 'Noi ci ___ seduti al tavolo.', correct: 'siamo', options: ['siamo', 'abbiamo'], difficulty: 'intermediate', type: 'reflexive' },

  // dovere/potere/volere + infinito — ausiliare del verbo seguente
  { sentence: 'Marco ___ dovuto studiare tutta la notte.', correct: 'ha', options: ['ha', 'è'], difficulty: 'intermediate', type: 'modal' },
  { sentence: 'Sara ___ dovuta andare in ufficio presto.', correct: 'è', options: ['è', 'ha'], difficulty: 'intermediate', type: 'modal' },
  { sentence: 'Io ___ voluto assaggiare quel dolce.', correct: 'ho', options: ['ho', 'sono'], difficulty: 'intermediate', type: 'modal' },
  { sentence: 'Luca ___ voluto partire subito.', correct: 'è', options: ['è', 'ha'], difficulty: 'intermediate', type: 'modal' },
  { sentence: 'Noi ___ dovuto finire il lavoro.', correct: 'abbiamo', options: ['abbiamo', 'siamo'], difficulty: 'intermediate', type: 'modal' },
  { sentence: 'Loro ___ potuti tornare a casa presto.', correct: 'sono', options: ['sono', 'hanno'], difficulty: 'intermediate', type: 'modal' },
  { sentence: 'Tu ___ dovuto aspettare molto?', correct: 'hai', options: ['hai', 'sei'], difficulty: 'intermediate', type: 'modal' },
  { sentence: 'Voi ___ voluto vedere quel film?', correct: 'avete', options: ['avete', 'siete'], difficulty: 'intermediate', type: 'modal' },
  { sentence: 'Lei ___ dovuta uscire presto.', correct: 'è', options: ['è', 'ha'], difficulty: 'intermediate', type: 'modal' },
  { sentence: 'Noi ___ voluto restare a casa.', correct: 'siamo', options: ['siamo', 'abbiamo'], difficulty: 'intermediate', type: 'modal' },

  // mixed practice
  { sentence: 'Il bambino ___ pianto tutta la notte.', correct: 'ha', difficulty: 'intermediate' },
  { sentence: 'La torta ___ costata troppo.', correct: 'è', difficulty: 'intermediate' },
  { sentence: 'Le foglie ___ cadute dagli alberi.', correct: 'sono', options: ['sono', 'hanno'], difficulty: 'intermediate' },
  { sentence: 'Il pacco ___ arrivato ieri.', correct: 'è', difficulty: 'intermediate' },
  { sentence: 'I turisti ___ visitato il museo.', correct: 'hanno', options: ['hanno', 'sono'], difficulty: 'intermediate' },
  { sentence: 'La squadra ___ vinto la partita.', correct: 'ha', difficulty: 'intermediate' },
  { sentence: "Le bambine ___ cresciute molto quest'anno.", correct: 'sono', options: ['sono', 'hanno'], difficulty: 'intermediate' },
  { sentence: 'Il concerto ___ cominciato in ritardo.', correct: 'è', difficulty: 'intermediate' },

  // ═══════════════ ADVANCED (25) ═══════════════
  // ambiguous verbs — same verb, two contexts (paired on purpose)
  { sentence: "L'uccellino ___ volato via dalla gabbia.", correct: 'è', difficulty: 'advanced', note: 'Volare (to fly) describes movement toward/away from a place here, so it takes essere.' },
  { sentence: "L'aereo ___ volato per due ore prima di atterrare.", correct: 'ha', difficulty: 'advanced', note: 'Volare (to fly) describes how long the action lasted here, so it takes avere.' },
  { sentence: 'Il bambino ___ corso a casa velocemente.', correct: 'è', difficulty: 'advanced', note: 'Correre (to run) describes movement toward a place here, so it takes essere.' },
  { sentence: "Il cane ___ corso per un'ora nel parco.", correct: 'ha', difficulty: 'advanced', note: 'Correre (to run) describes how long the action lasted here, so it takes avere.' },
  { sentence: "Io ___ passato tutta l'estate al mare.", correct: 'ho', options: ['ho', 'sono'], difficulty: 'advanced', note: 'Passare (to spend time) is followed by a length of time here, so it takes avere.' },
  { sentence: 'Marco ___ passato dal mio ufficio ieri.', correct: 'è', difficulty: 'advanced', note: 'Passare (to stop by) describes moving from one place to another here, so it takes essere.' },
  { sentence: 'Il tempo ___ cambiato improvvisamente.', correct: 'è', difficulty: 'advanced', note: 'Cambiare (to change) has no direct object here — it means "to become different" — so it takes essere.' },
  { sentence: 'Marta ___ cambiato idea.', correct: 'ha', difficulty: 'advanced', note: 'Cambiare (to change) has a direct object here (idea), so it takes avere.' },
  { sentence: 'Loro ___ vissuti a Roma per dieci anni.', correct: 'sono', options: ['sono', 'hanno'], difficulty: 'advanced', note: 'Vivere (to live) describes where someone resided for a period of time here, so it usually takes essere.' },
  { sentence: "Lei ___ vissuto un'esperienza incredibile.", correct: 'ha', difficulty: 'advanced', note: "Vivere (to experience) has a direct object here (un'esperienza), so it takes avere." },
  { sentence: 'Il gatto ___ saltato sul tavolo.', correct: 'è', difficulty: 'advanced', note: 'Saltare (to jump) describes movement toward a place here, so it takes essere.' },
  { sentence: 'Il canguro ___ saltato per tutto il prato.', correct: 'ha', difficulty: 'advanced', note: 'Saltare (to jump) describes a repeated/ongoing action here, so it takes avere.' },

  // modal + reflexive interactions
  { sentence: 'Mi ___ dovuto svegliare presto per il volo.', correct: 'sono', options: ['sono', 'ho'], difficulty: 'advanced', type: 'modalReflexive' },
  { sentence: 'Marco si ___ voluto vestire elegante per la festa.', correct: 'è', options: ['è', 'ha'], difficulty: 'advanced', type: 'modalReflexive' },
  { sentence: 'Ci ___ dovuti alzare prestissimo stamattina.', correct: 'siamo', options: ['siamo', 'abbiamo'], difficulty: 'advanced', type: 'modalReflexive' },
  { sentence: 'Non ___ potuto ricordarmi il suo nome.', correct: 'ho', options: ['ho', 'sono'], difficulty: 'advanced', type: 'modalReflexiveAttached' },
  { sentence: "Lei si ___ dovuta preoccupare per l'esame.", correct: 'è', options: ['è', 'ha'], difficulty: 'advanced', type: 'modalReflexive' },
  { sentence: 'Non ___ voluto svegliarla troppo presto.', correct: 'ho', options: ['ho', 'sono'], difficulty: 'advanced', type: 'transitive' },
  { sentence: 'Voi ___ dovuto partire presto stamattina.', correct: 'siete', options: ['siete', 'avete'], difficulty: 'advanced', type: 'modal' },
];

const getAuxiliary = (verb) => {
  if (verb.ausiliare) return verb.ausiliare; // eventuale override manuale futuro nel dizionario
  const name = Array.isArray(verb.verbo) ? verb.verbo[0] : verb.verbo;
  const firstWord = name.split(' ')[0];
  if (/si$/i.test(firstWord)) return 'essere'; // riflessivi: Alzarsi, Divertirsi, Vestirsi...
  if (ESSERE_VERBS.includes(name)) return 'essere';
  return 'avere';
};


// ─── Helpers ──────────────────────────────────────────────────────────────────
const getPool = (difficulty, mode) => {
  let base;
  if (difficulty === 'beginner') {
    base = VERB_DICTIONARY.filter(v => {
      const name = Array.isArray(v.verbo) ? v.verbo[0] : v.verbo;
      return BEGINNER_VERBS.includes(name);
    });
  } else if (difficulty === 'intermediate') {
    base = VERB_DICTIONARY.filter(v => {
      const name = Array.isArray(v.verbo) ? v.verbo[0] : v.verbo;
      return INTERMEDIATE_FULL_LIST.includes(name);
    });
  } else {
    base = VERB_DICTIONARY;
  }
  if (mode === 'participio') return base.filter(v => !!v.participio);
  if (mode === 'futuro' || mode === 'futuro e condizionale') return base.filter(v => !!v.futuro);
  if (mode === 'ausiliare') {
    return base.filter(v => {
      const name = Array.isArray(v.verbo) ? v.verbo[0] : v.verbo;
      return !AUXILIARY_AMBIGUOUS.includes(name);
    });
  }
  return base;
};

const getCorrectAnswer = (verb, mode) => {
    if (mode === 'frasi al passato prossimo') return verb.correct;
  const activeMode = mode === 'futuro e condizionale' ? 'futuro' : mode;
  if (activeMode === 'infinito' || activeMode === 'riconoscimento') {
    return Array.isArray(verb.verbo) ? verb.verbo[0] : verb.verbo;
  }
  if (activeMode === 'participio') return verb.participio;
  if (activeMode === 'futuro') return verb.futuro;
   if (activeMode === 'ausiliare') return getAuxiliary(verb) === 'essere' ? 'Essere' : 'Avere';
  return verb.verbo;
};

const getQuestionDisplay = (verb, mode) => {
  if (mode === 'frasi al passato prossimo') return verb.sentence;
  if (mode === 'riconoscimento' || mode === 'infinito') return `"${verb.translation.toLowerCase()}"`;
  return (Array.isArray(verb.verbo) ? verb.verbo[0] : verb.verbo).toLowerCase();
};

const getWrongOptions = (correctVerb, pool, mode, count = 3) => {
  const activeMode = mode === 'futuro e condizionale' ? 'futuro' : mode;
  if (activeMode !== 'riconoscimento') {
    const customKey = `${activeMode}_options`;
    if (correctVerb[customKey]?.length > 0) return correctVerb[customKey].slice(0, count);
  }
  if (activeMode === 'riconoscimento') {
    const correctVal = Array.isArray(correctVerb.verbo) ? correctVerb.verbo[0] : correctVerb.verbo;
    return pool
      .filter(v => {
        const verbVal = Array.isArray(v.verbo) ? v.verbo[0] : v.verbo;
        return verbVal.toLowerCase() !== correctVal.toLowerCase();
      })
      .sort(() => Math.random() - 0.5)
      .slice(0, count)
      .map(v => Array.isArray(v.verbo) ? v.verbo[0] : v.verbo);
  }
  const correctForm = getCorrectAnswer(correctVerb, mode);
  return pool
    .filter(v => {
      const form = getCorrectAnswer(v, mode);
      return form && form.toLowerCase() !== correctForm.toLowerCase();
    })
    .sort(() => Math.random() - 0.5)
    .slice(0, count)
    .map(v => getCorrectAnswer(v, mode))
    .filter(Boolean);
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const T = '#BC6C25';
const G = '#283618';

const QuizStyles = () => (
  <style>{`
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes popIn {
      0%   { transform: scale(0.88); opacity: 0; }
      60%  { transform: scale(1.04); }
      100% { transform: scale(1);    opacity: 1; }
    }
    @keyframes correctPulse {
      0%   { box-shadow: 0 0 0 0 rgba(96,108,56,0.4); }
      70%  { box-shadow: 0 0 0 10px rgba(96,108,56,0); }
      100% { box-shadow: 0 0 0 0 rgba(96,108,56,0); }
    }
    @keyframes wrongShake {
      0%,100% { transform: translateX(0); }
      20%     { transform: translateX(-6px); }
      40%     { transform: translateX(6px); }
      60%     { transform: translateX(-4px); }
      80%     { transform: translateX(4px); }
    }
    @keyframes shimmer {
      0%   { background-position: -400px 0; }
      100% { background-position: 400px 0; }
    }

    .vq-fade { animation: fadeUp 0.4s ease both; }
    .vq-pop  { animation: popIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both; }

    .vq-opt {
      transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
      cursor: pointer;
    }
    .vq-opt:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(40,54,24,0.1);
    }
    .vq-opt.correct-anim { animation: correctPulse 0.6s ease forwards; }
    .vq-opt.wrong-anim   { animation: wrongShake 0.45s ease forwards; }

    .vq-level-card {
      transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
      cursor: pointer;
    }
    .vq-level-card:hover {
      transform: translateY(-3px);
      box-shadow: 0 12px 32px rgba(40,54,24,0.1);
    }

    .vq-mode-card {
      transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
      cursor: pointer;
    }
    .vq-mode-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(40,54,24,0.08);
    }

    .vq-start-btn {
      background: ${T}; color: #fff; border: none;
      padding: 16px 48px; border-radius: 50px;
      font-size: 0.78rem; font-weight: 700; letter-spacing: 2.5px;
      text-transform: uppercase; cursor: pointer;
      font-family: 'Montserrat', sans-serif;
      transition: all 0.25s ease;
      box-shadow: 0 6px 20px rgba(188,108,37,0.3);
    }
    .vq-start-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 28px rgba(188,108,37,0.4);
    }
    .vq-start-btn:disabled {
      opacity: 0.35; cursor: default; transform: none;
      box-shadow: none;
    }

    .vq-submit-btn {
      background: none; border: 1.5px solid ${T}; color: ${T};
      padding: 11px 38px; border-radius: 50px;
      font-size: 0.7rem; text-transform: uppercase; letter-spacing: 2.5px;
      cursor: pointer; font-weight: 700; font-family: 'Montserrat', sans-serif;
      transition: all 0.25s ease;
    }
    .vq-submit-btn:hover:not(:disabled) {
      background: ${T}; color: #fff;
      box-shadow: 0 4px 14px rgba(188,108,37,0.25);
    }
    .vq-submit-btn:disabled { opacity: 0.4; cursor: default; }

    .vq-restart-btn {
      background: none; border: 1.5px solid ${G}; color: ${G};
      padding: 12px 40px; border-radius: 50px;
      font-size: 0.7rem; letter-spacing: 2.5px; text-transform: uppercase;
      cursor: pointer; font-weight: 700; font-family: 'Montserrat', sans-serif;
      transition: all 0.25s ease; margin-top: 32px;
    }
    .vq-restart-btn:hover {
      background: ${G}; color: #fff;
      box-shadow: 0 4px 14px rgba(40,54,24,0.2);
    }

    .vq-hint-link {
      background: none; border: none; cursor: pointer;
      color: ${T}; font-size: 0.68rem; letter-spacing: 1.5px;
      text-transform: uppercase; text-decoration: underline;
      text-underline-offset: 3px; font-family: 'Montserrat', sans-serif;
      opacity: 0.7; transition: opacity 0.2s; font-weight: 600; padding: 0;
    }
    .vq-hint-link:hover { opacity: 1; }

    .dict-scroll::-webkit-scrollbar       { width: 4px; }
    .dict-scroll::-webkit-scrollbar-track { background: transparent; }
    .dict-scroll::-webkit-scrollbar-thumb { background: rgba(188,108,37,0.3); border-radius: 4px; }
  `}</style>
);

// ─── Onboarding Screen ────────────────────────────────────────────────────────
const OnboardingScreen = ({ onStart, containerRef }) => {
  const [step, setStep]           = useState(1); // 1 = level, 2 = mode
  const [difficulty, setDifficulty] = useState(null);
  const [mode, setMode]           = useState(null);

  const levels = [
    {
      id: 'beginner',
      emoji: '🌱',
      label: 'Beginner',
      color: '#606C38',
      bg: 'rgba(96,108,56,0.06)',
      border: 'rgba(96,108,56,0.2)',
      activeBorder: '#606C38',
      desc: `${BEGINNER_VERBS.length} essential verbs · the foundation`,
    },
    {
      id: 'intermediate',
      emoji: '🔥',
      label: 'Intermediate',
      color: T,
      bg: 'rgba(188,108,37,0.06)',
      border: 'rgba(188,108,37,0.2)',
      activeBorder: T,
      desc: `${INTERMEDIATE_FULL_LIST.length} verbs · building fluency`,
    },
    {
      id: 'advanced',
      emoji: '⚡',
      label: 'Advanced',
      color: G,
      bg: 'rgba(40,54,24,0.06)',
      border: 'rgba(40,54,24,0.15)',
      activeBorder: G,
      desc: `${VERB_DICTIONARY.length} verbs · full immersion`,
    },
  ];

  const modes = [
    {
      id: 'riconoscimento',
      emoji: '🌿',
      label: 'Recognition',
      desc: 'See the English meaning, pick the Italian verb.',
    },
    {
      id: 'participio',
      emoji: '🟡',
      label: 'Past Participle',
      desc: 'See the verb, choose the correct past participle.',
    },
    {
      id: 'ausiliare',
      emoji: '🔑',
      label: 'Avere o Essere',
      desc: 'See the verb, choose the auxiliary for passato prossimo.',
    },
    {
      id: 'frasi al passato prossimo',
      emoji: '📝',
      label: 'Ha o È?',
      desc: 'Complete the sentence with the correct auxiliary verb.',
    },
    {
      id: 'futuro e condizionale',
      emoji: '🟠',
      label: 'Future Stem',
      desc: 'See the verb, choose the future/conditional stem.',
    },
    {
      id: 'infinito',
      emoji: '✍️',
      label: 'Type it',
      desc: 'See the English meaning, type the Italian verb.',
    },
  ];

  return (
    <div ref={containerRef} style={{ maxWidth: 580, margin: '0 auto', padding: '20px 16px', fontFamily: "'Montserrat', sans-serif" }}>
      <QuizStyles />

      {/* Header */}
      <div className="vq-fade" style={{ textAlign: 'center', marginBottom: 40 }}>
        <p style={{ color: T, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '4px', fontWeight: 600, marginBottom: 8 }}>
          LA TUA VOCE
        </p>
        <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.6rem, 5vw, 2.2rem)', lineHeight: 1.2, color: G, margin: '0 0 6px', fontWeight: 700 }}>
          Building your own voice
          <br />
          <span style={{ fontStyle: 'italic', color: T, fontWeight: 400 }}>
            one <strong>verb</strong> at a time
          </span>
        </h3>
        <div style={{ width: 40, height: 1, backgroundColor: T, margin: '16px auto 0', opacity: 0.75 }} />
      </div>

      {/* Step 1 — Level */}
      <div className="vq-fade" style={{ animationDelay: '0.1s' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            backgroundColor: step >= 1 ? T : '#eee',
            color: step >= 1 ? '#fff' : '#aaa',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.72rem', fontWeight: 700, flexShrink: 0,
            transition: 'background 0.3s ease',
          }}>1</div>
          <p style={{ margin: 0, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: G }}>
            Choose your level
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 32 }}>
          {levels.map(l => (
            <button
              key={l.id}
              className="vq-level-card"
              onClick={() => { setDifficulty(l.id); if (step < 2) setStep(2); }}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: 8, padding: '18px 10px',
                borderRadius: 14,
                border: `2px solid ${difficulty === l.id ? l.activeBorder : l.border}`,
                backgroundColor: difficulty === l.id ? l.bg : '#fff',
                boxShadow: difficulty === l.id ? `0 4px 16px ${l.bg}` : 'none',
                fontFamily: "'Montserrat', sans-serif",
                textAlign: 'center',
              }}
            >
              <span style={{ fontSize: '1.6rem' }}>{l.emoji}</span>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: difficulty === l.id ? l.color : G, letterSpacing: '0.5px' }}>
                {l.label}
              </span>
              <span style={{ fontSize: '0.6rem', color: '#999', lineHeight: 1.4 }}>{l.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Step 2 — Mode */}
      <div
        className="vq-fade"
        style={{
          animationDelay: '0.15s',
          opacity: step >= 2 ? 1 : 0.35,
          pointerEvents: step >= 2 ? 'auto' : 'none',
          transition: 'opacity 0.3s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            backgroundColor: step >= 2 ? T : '#eee',
            color: step >= 2 ? '#fff' : '#aaa',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.72rem', fontWeight: 700, flexShrink: 0,
            transition: 'background 0.3s ease',
          }}>2</div>
          <p style={{ margin: 0, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: G }}>
            How do you want to practice?
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 36 }}>
          {modes.map(m => (
            <button
              key={m.id}
              className="vq-mode-card"
              onClick={() => setMode(m.id)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                gap: 6, padding: '16px 14px',
                borderRadius: 14,
                border: `2px solid ${mode === m.id ? T : 'rgba(188,108,37,0.15)'}`,
                backgroundColor: mode === m.id ? 'rgba(188,108,37,0.06)' : '#fff',
                fontFamily: "'Montserrat', sans-serif",
                textAlign: 'left',
              }}
            >
              <span style={{ fontSize: '1.3rem' }}>{m.emoji}</span>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: mode === m.id ? T : G }}>
                {m.label}
              </span>
              <span style={{ fontSize: '0.62rem', color: '#888', lineHeight: 1.5 }}>{m.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Start button */}
      <div style={{ textAlign: 'center' }}>
        <button
          className="vq-start-btn"
          disabled={!difficulty || !mode}
          onClick={() => onStart(difficulty, mode)}
        >
          Inizia →
        </button>
        {(!difficulty || !mode) && (
          <p style={{ marginTop: 12, fontSize: '0.65rem', color: '#bbb', letterSpacing: '1px' }}>
            {!difficulty ? 'Pick a level to continue' : 'Pick a practice type to continue'}
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Quiz Screen ──────────────────────────────────────────────────────────────
const QuizScreen = ({ difficulty, mode, firstName, onBack, containerRef }) => {
  const [currentVerb,    setCurrentVerb]    = useState(null);
  const [userInput,      setUserInput]      = useState('');
  const [feedback,       setFeedback]       = useState(null);
  const [options,        setOptions]        = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showHint,       setShowHint]       = useState(false);
  const [verbsCount,     setVerbsCount]     = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [isFinished,     setIsFinished]     = useState(false);
  const [showDictionary, setShowDictionary] = useState(false);
  const [searchTerm,     setSearchTerm]     = useState('');
  const [cardKey,        setCardKey]        = useState(0);
  const [streak,         setStreak]         = useState(0);
  const [bestStreak,     setBestStreak]     = useState(0);
  const [missedItems,    setMissedItems]    = useState([]);
  const [correctMessage, setCorrectMessage] = useState(CORRECT_MESSAGES[0]);
  const [milestone,      setMilestone]      = useState(null);
  const [answerHistory,  setAnswerHistory]  = useState([]);

  const usedKeysRef = useRef(new Set());
const getItemKey = (item, currentMode) =>
  currentMode === 'frasi al passato prossimo' ? item.sentence : (Array.isArray(item.verbo) ? item.verbo[0] : item.verbo);

  const TOTAL_VERBS = 10;
  const isMultipleChoice = mode !== 'infinito';

const pickRandomVerb = (currentMode = mode, currentDiff = difficulty) => {
    if (currentMode === 'frasi al passato prossimo') {
      const pool = SENTENCE_EXERCISES.filter(s => s.difficulty === currentDiff);
      let available = pool.filter(s => !usedKeysRef.current.has(s.sentence));
      if (available.length === 0) { usedKeysRef.current.clear(); available = pool; }
      const random = available[Math.floor(Math.random() * available.length)];
      usedKeysRef.current.add(random.sentence);
      setCurrentVerb(random);
      setUserInput('');
      setFeedback(null);
      setSelectedOption(null);
      setShowHint(false);
      setCardKey(k => k + 1);
      setOptions((random.options || ['ha', 'è']).slice().sort(() => Math.random() - 0.5));
      return;
    }
    const pool = getPool(currentDiff, currentMode);
    let available = pool.filter(v => !usedKeysRef.current.has(getItemKey(v, currentMode)));
    if (available.length === 0) { usedKeysRef.current.clear(); available = pool; }
    const random = available[Math.floor(Math.random() * available.length)];
    usedKeysRef.current.add(getItemKey(random, currentMode));
    setCurrentVerb(random);
    setUserInput('');
    setFeedback(null);
    setSelectedOption(null);
    setShowHint(false);
    setCardKey(k => k + 1);
    if (currentMode !== 'infinito') {
      if (currentMode === 'ausiliare') {
        setOptions(['Avere', 'Essere'].sort(() => Math.random() - 0.5));
      } else {
        const correct = getCorrectAnswer(random, currentMode);
        const wrong   = getWrongOptions(random, pool, currentMode);
        setOptions([...wrong, correct].sort(() => Math.random() - 0.5));
      }
    }
  };

  useEffect(() => { pickRandomVerb(); }, []); // eslint-disable-line

  const moveToNext = () => {
    if (verbsCount + 1 >= TOTAL_VERBS) {
      setIsFinished(true);
    } else {
      setVerbsCount(p => p + 1);
      pickRandomVerb();
    }
  };

const handleOptionSelect = (option) => {
    if (feedback) return;
    setSelectedOption(option);
    const correct   = getCorrectAnswer(currentVerb, mode);
    const isCorrect = option.toLowerCase() === correct.toLowerCase();
    setFeedback(isCorrect ? 'correct' : 'error');
    setAnswerHistory(prev => [...prev, isCorrect]);
    if (isCorrect) {
      setCorrectAnswers(p => p + 1);
      setCorrectMessage(CORRECT_MESSAGES[Math.floor(Math.random() * CORRECT_MESSAGES.length)]);
      setStreak(p => {
        const next = p + 1;
        setBestStreak(b => Math.max(b, next));
        if (STREAK_MILESTONES[next]) {
          setMilestone(STREAK_MILESTONES[next]);
          setTimeout(() => setMilestone(null), 2600);
        }
        return next;
      });
      setTimeout(moveToNext, 1200);
    } else {
      setStreak(0);
      setMissedItems(prev => [...prev, { question: getQuestionDisplay(currentVerb, mode), correctAnswer: correct }]);
    }
    // wrong answers wait for the student to click "Continua"
  };

const handleCheck = (e) => {
    e.preventDefault();
    if (!userInput.trim() || feedback) return;
    const clean    = userInput.toLowerCase().trim().replace('-', '');
    const accepted = (Array.isArray(currentVerb.verbo) ? currentVerb.verbo : [currentVerb.verbo])
      .map(v => v.toLowerCase().trim().replace('-', ''));
    const isCorrect = accepted.includes(clean);
    setFeedback(isCorrect ? 'correct' : 'error');
    setAnswerHistory(prev => [...prev, isCorrect]);
    if (isCorrect) {
      setCorrectAnswers(p => p + 1);
      setCorrectMessage(CORRECT_MESSAGES[Math.floor(Math.random() * CORRECT_MESSAGES.length)]);
      setStreak(p => {
        const next = p + 1;
        setBestStreak(b => Math.max(b, next));
        if (STREAK_MILESTONES[next]) {
          setMilestone(STREAK_MILESTONES[next]);
          setTimeout(() => setMilestone(null), 2600);
        }
        return next;
      });
      setTimeout(moveToNext, 1200);
    } else {
      setStreak(0);
      setMissedItems(prev => [...prev, { question: getQuestionDisplay(currentVerb, mode), correctAnswer: getCorrectAnswer(currentVerb, mode) }]);
    }
  };

  const filteredVerbs = VERB_DICTIONARY.filter(v =>
    (Array.isArray(v.verbo) ? v.verbo.join(' ') : v.verbo)
      .toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.translation.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const correctAnswer = currentVerb ? getCorrectAnswer(currentVerb, mode) : '';

  // ── Score screen ──────────────────────────────────────────────────────────
  if (isFinished) {
    const name = firstName || 'Studente';
    const pct  = Math.round((correctAnswers / TOTAL_VERBS) * 100);
    const msg  =
      pct >= 80 ? `Eccellente, ${name}. Simply perfect.`
      : pct >= 50 ? `Ottimo, ${name}. You've almost mastered this set.`
      : `Non mollare, ${name}. It's all part of the journey.`;

    return (
      <div style={{ maxWidth: 580, margin: '0 auto', padding: '20px 16px', fontFamily: "'Montserrat', sans-serif" }}>
        <QuizStyles />
        <div className="vq-fade" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p style={{ color: T, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '4px', fontWeight: 600, marginBottom: 24 }}>
            Sessione Completata
          </p>

          <div style={{ position: 'relative', width: 130, height: 130, margin: '0 auto 28px' }}>
            <svg width="130" height="130" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="65" cy="65" r="56" fill="none" stroke="#f0f0f0" strokeWidth="6" />
              <circle
                cx="65" cy="65" r="56" fill="none"
                stroke={pct >= 80 ? '#606C38' : pct >= 50 ? T : '#B23A3A'}
                strokeWidth="6"
                strokeDasharray={`${2 * Math.PI * 56}`}
                strokeDashoffset={`${2 * Math.PI * 56 * (1 - pct / 100)}`}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 1s ease' }}
              />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '2.2rem', fontWeight: 300, color: G, lineHeight: 1 }}>
                {correctAnswers}
              </span>
              <span style={{ fontSize: '0.6rem', color: T, letterSpacing: '2px', textTransform: 'uppercase' }}>
                / {TOTAL_VERBS}
              </span>
            </div>
          </div>

<p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.25rem', fontStyle: 'italic', color: '#444', maxWidth: 320, margin: '0 auto 8px' }}>
            {msg}
          </p>

          {bestStreak >= 3 && (
            <p style={{ fontSize: '0.68rem', letterSpacing: '1px', color: T, fontWeight: 700, marginTop: 4 }}>
              🔥 Best streak: {bestStreak} in a row
            </p>
          )}

          {missedItems.length > 0 && (
            <div style={{ textAlign: 'left', maxWidth: 420, margin: '28px auto 0', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 14, padding: '18px 20px' }}>
              <p style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '3px', color: '#B23A3A', fontWeight: 700, marginBottom: 12 }}>
                Da ripassare
              </p>
              {missedItems.map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '8px 0', borderTop: i > 0 ? '1px solid #eee' : 'none' }}>
                  <span style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: '0.95rem', color: '#444' }}>
                    {item.question}
                  </span>
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#606C38', whiteSpace: 'nowrap' }}>
                    {item.correctAnswer}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 32 }}>
<button className="vq-restart-btn" style={{ margin: 0 }} onClick={() => { usedKeysRef.current.clear(); setVerbsCount(0); setCorrectAnswers(0); setStreak(0); setMissedItems([]); setAnswerHistory([]); setMilestone(null); setIsFinished(false); pickRandomVerb(); }}>
              Riprova
            </button>
            <button
              onClick={onBack}
              style={{ background: 'none', border: `1.5px solid ${T}`, color: T, padding: '12px 32px', borderRadius: '50px', fontSize: '0.7rem', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer', fontWeight: 700, fontFamily: "'Montserrat', sans-serif', marginTop: 0" }}
            >
              Change settings
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentVerb) return null;

  const modeLabels = {
    'riconoscimento':        'Come si dice in italiano:',
    'participio':            'Participio passato di:',
    'ausiliare':             'Avere o Essere per il passato prossimo:',
    'frasi al passato prossimo':                 'Completa la frase:',
    'futuro e condizionale': 'Radice futuro/condizionale di:',
    'infinito':              'Come si dice in italiano:',
  };

  const diffLabel = { beginner: '🌱 Beginner', intermediate: '🔥 Intermediate', advanced: '⚡ Advanced' }[difficulty];
  const modeShort = { 'riconoscimento': 'Recognition', 'participio': 'Past Participle', 'futuro e condizionale': 'Future Stem', 'ausiliare': 'Avere o Essere', 'infinito': 'Type it', 'frasi al passato prossimo': 'Ha o È?' }[mode];

  return (
    <div ref={containerRef} style={{ maxWidth: 580, margin: '0 auto', padding: '20px 16px', fontFamily: "'Montserrat', sans-serif" }}>
      <QuizStyles />

      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '1px', color: '#606C38', backgroundColor: 'rgba(96,108,56,0.1)', padding: '4px 12px', borderRadius: 20 }}>
            {diffLabel}
          </span>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '1px', color: T, backgroundColor: 'rgba(188,108,37,0.1)', padding: '4px 12px', borderRadius: 20 }}>
            {modeShort}
          </span>
        </div>
        <button
          onClick={onBack}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.68rem', color: '#aaa', fontFamily: "'Montserrat', sans-serif", fontWeight: 600, letterSpacing: '1px', textDecoration: 'underline', textUnderlineOffset: 3, padding: 0 }}
        >
          ⚙️ Change
        </button>
      </div>

      {/* Dictionary toggle */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
        <button
          onClick={() => setShowDictionary(v => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 20px', borderRadius: 50,
            fontSize: '0.65rem', letterSpacing: '1.5px', textTransform: 'uppercase',
            fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
            transition: 'all 0.2s ease',
            background: showDictionary ? G : '#fcfcfc',
            color:      showDictionary ? '#fff' : G,
            border:     showDictionary ? `1.5px solid ${G}` : '1.5px solid #eee',
          }}
        >
          <span>{showDictionary ? '✕' : '📚'}</span>
          {showDictionary ? 'Chiudi dizionario' : 'Dizionario dei Verbi'}
        </button>
      </div>

      {/* Dictionary panel */}
      {showDictionary && (
        <div className="vq-fade" style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 16, padding: '24px 20px', marginBottom: 28, boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
          <p style={{ color: T, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '4px', fontWeight: 600, marginBottom: 8 }}>Dizionario</p>
          <input
            placeholder="Cerca un verbo..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '10px 0', marginBottom: 18, border: 'none', borderBottom: `1px solid ${T}`, outline: 'none', fontSize: '0.88rem', fontFamily: "'Montserrat', sans-serif", color: G, background: 'transparent', boxSizing: 'border-box' }}
          />
          <div className="dict-scroll" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px,1fr))', gap: 12, maxHeight: 260, overflowY: 'auto', paddingRight: 4 }}>
            {filteredVerbs.map((v, i) => (
              <div key={i} style={{ fontSize: '0.82rem', padding: '10px 12px', background: '#fafafa', borderRadius: 8, border: '1px solid #f5f5f5' }}>
                <div style={{ fontWeight: 600, color: G, marginBottom: 4 }}>
                  {(Array.isArray(v.verbo) ? v.verbo[0] : v.verbo).toUpperCase()}
                  <span style={{ fontWeight: 300, opacity: 0.75 }}> · {v.translation}</span>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {v.participio && (
                    <span style={{ fontSize: '0.68rem', background: '#fff', border: `1px solid ${v.participio_irregolare ? 'rgba(178,58,58,0.3)' : '#eee'}`, padding: '2px 8px', borderRadius: 20, color: '#444' }}>
                      <strong style={{ color: T, marginRight: 3, fontSize: '0.6rem' }}>P:</strong>{v.participio}
                      {v.participio_irregolare && <span style={{ marginLeft: 4, fontSize: '0.55rem', color: '#B23A3A', fontWeight: 700 }}>★</span>}
                    </span>
                  )}
                  {v.futuro && (
                    <span style={{ fontSize: '0.68rem', background: '#fff', border: `1px solid ${v.futuro_irregolare ? 'rgba(178,58,58,0.3)' : '#eee'}`, padding: '2px 8px', borderRadius: 20, color: '#444' }}>
                      <strong style={{ color: T, marginRight: 3, fontSize: '0.6rem' }}>F:</strong>{v.futuro}
                      {v.futuro_irregolare && <span style={{ marginLeft: 4, fontSize: '0.55rem', color: '#B23A3A', fontWeight: 700 }}>★</span>}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

           {/* Milestone banner */}
      {milestone && (
        <div className="vq-pop" style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: `linear-gradient(90deg, ${T}, #D4A373)`, color: '#fff',
            padding: '10px 24px', borderRadius: 24,
            fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.5px',
            fontFamily: "'Montserrat', sans-serif",
            boxShadow: '0 6px 18px rgba(188,108,37,0.35)',
          }}>
            {milestone}
          </span>
        </div>
      )}

      {/* Streak badge */}
      {!milestone && streak >= 2 && (
        <div className="vq-pop" style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(188,108,37,0.1)', color: T,
            padding: '5px 16px', borderRadius: 20,
            fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px',
            fontFamily: "'Montserrat', sans-serif",
          }}>
            🔥 {streak} in a row
          </span>
        </div>
      )}

      {/* Progress dots */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 36, flexWrap: 'wrap' }}>
        {Array.from({ length: TOTAL_VERBS }).map((_, i) => {
          const result = answerHistory[i]; // undefined = not yet answered, true = correct, false = wrong
          const isCurrent = i === verbsCount && result === undefined;
          let bg = '#eee';
          if (result === true) bg = '#606C38';
          else if (result === false) bg = '#B23A3A';
          else if (isCurrent) bg = T;
          return (
            <span
              key={i}
              style={{
                width: 9, height: 9, borderRadius: '50%',
                backgroundColor: bg,
                opacity: result === undefined && !isCurrent ? 0.5 : 1,
                transform: isCurrent ? 'scale(1.3)' : 'scale(1)',
                transition: 'all 0.3s ease',
              }}
            />
          );
        })}
      </div>

      {/* Quiz card */}
      <div
        key={cardKey}
        className="vq-fade"
        style={{
          borderRadius: 20,
          padding: 'clamp(24px, 5vw, 44px)',
          border: '1px solid #f0f0f0',
          boxShadow: '0 8px 32px rgba(40,54,24,0.04)',
          background: feedback === 'correct' ? '#f6faf2' : feedback === 'error' ? '#fff9f9' : '#fff',
          transition: 'background 0.35s ease',
        }}
      >
        {/* Counter */}
        <div style={{ marginBottom: 28 }}>
          <span style={{ color: T, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '4px', fontWeight: 600 }}>
            Verbo {verbsCount + 1} / {TOTAL_VERBS}
          </span>
        </div>

        {/* Question */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <span style={{ display: 'block', fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: '#666', marginBottom: 14 }}>
            {modeLabels[mode]}
          </span>
<h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: mode === 'frasi al passato prossimo' ? 'clamp(1.3rem, 4vw, 1.8rem)' : 'clamp(2rem, 6vw, 3rem)', fontWeight: 400, fontStyle: 'italic', color: G, margin: 0, lineHeight: 1.3 }}>
            {mode === 'frasi al passato prossimo'
              ? currentVerb.sentence
              : mode === 'riconoscimento' || mode === 'infinito'
                ? `"${currentVerb.translation.toLowerCase()}"`
                : (Array.isArray(currentVerb.verbo) ? currentVerb.verbo[0] : currentVerb.verbo).toLowerCase()
            }
          </h2>
        </div>

        {/* Multiple choice */}
        {isMultipleChoice && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, maxWidth: 420, margin: '0 auto 10px' }}>
            {options.map((opt, i) => {
              const isSelected = selectedOption === opt;
              const isRight    = opt.toLowerCase() === correctAnswer.toLowerCase();
              let bg = '#fafafa', border = '1.5px solid #eee', color = G;
              let animClass = 'vq-opt';
              if (feedback) {
                if (isSelected && feedback === 'correct') { bg = '#f0f7ec'; border = '1.5px solid #606C38'; color = '#606C38'; animClass += ' correct-anim'; }
                else if (isSelected && feedback === 'error') { bg = '#fff5f5'; border = '1.5px solid #B23A3A'; color = '#B23A3A'; animClass += ' wrong-anim'; }
                else if (!isSelected && isRight && feedback === 'error') { bg = '#f0f7ec'; border = '1.5px solid #606C38'; color = '#606C38'; }
              }
              return (
                <button key={i} className={animClass} disabled={!!feedback} onClick={() => handleOptionSelect(opt)}
                  style={{ backgroundColor: bg, border, color, padding: '16px 14px', borderRadius: 12, fontSize: '0.95rem', fontWeight: 600, cursor: feedback ? 'default' : 'pointer', fontFamily: "'Montserrat', sans-serif", letterSpacing: '0.5px', lineHeight: 1.3 }}>
                  {opt}
                </button>
              );
            })}
          </div>
        )}

        {/* Type-in */}
        {!isMultipleChoice && (
          <form onSubmit={handleCheck} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
            <input
              value={userInput}
              onChange={e => setUserInput(e.target.value)}
              placeholder="scrivi in italiano..."
              autoFocus autoComplete="off" disabled={!!feedback}
              style={{
                width: '100%', maxWidth: 340, background: 'transparent', border: 'none',
                borderBottom: `1.5px solid ${feedback === 'correct' ? '#606C38' : feedback === 'error' ? '#B23A3A' : '#e0e0e0'}`,
                padding: '12px 0', fontSize: '1.4rem', fontWeight: 600,
                textAlign: 'center', outline: 'none', color: G,
                fontFamily: "'Montserrat', sans-serif", transition: 'border-color 0.3s ease',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <button type="submit" className="vq-submit-btn" disabled={!!feedback}>Controlla</button>
              {!feedback && !showHint && (
                <button type="button" className="vq-hint-link" onClick={() => setShowHint(true)}>suggerimento</button>
              )}
              {showHint && (
                <span style={{ fontSize: '0.78rem', color: T, letterSpacing: '1px' }}>
                  inizia con <strong style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: '1rem' }}>{correctAnswer.slice(0, 3)}…</strong>
                </span>
              )}
            </div>
          </form>
        )}

        {/* Feedback */}
        <div style={{ minHeight: 72, marginTop: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
{feedback === 'correct' && (
            <div className="vq-pop">
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.5rem', fontStyle: 'italic', color: '#606C38', margin: '0 0 4px' }}>{correctMessage}</p>
              <p style={{ fontSize: '0.6rem', letterSpacing: '3px', textTransform: 'uppercase', color: '#606C38', opacity: 0.7, margin: 0 }}>risposta esatta</p>
            </div>
          )}
{feedback === 'error' && (
  <div className="vq-pop">
    <p style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: '#B23A3A', margin: '0 0 8px' }}>Riprova</p>
    <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem', color: '#B23A3A', margin: 0 }}>
      La risposta è{' '}
      <em style={{ fontWeight: 600, borderBottom: '1.5px solid #B23A3A', paddingBottom: 1 }}>{correctAnswer}</em>
    </p>
{mode === 'frasi al passato prossimo' && (currentVerb.note || NOTE_TEXTS[currentVerb.type]) && (
  <p style={{ fontSize: '0.72rem', color: '#888', marginTop: 10, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
    💡 {currentVerb.note || NOTE_TEXTS[currentVerb.type]}
  </p>
)}
    <button
      onClick={moveToNext}
      style={{
        marginTop: 18, background: '#B23A3A', color: '#fff', border: 'none',
        padding: '10px 32px', borderRadius: 50, fontSize: '0.68rem', fontWeight: 700,
        letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer',
        fontFamily: "'Montserrat', sans-serif",
      }}
    >
      Continua →
    </button>
  </div>
)}
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: 40, opacity: 0.2 }}>
        <span style={{ fontSize: '0.7rem', letterSpacing: '6px', color: T }}>· · ·</span>
      </div>
    </div>
  );
};

// ─── Root Component ───────────────────────────────────────────────────────────
export const VerbQuiz = ({ firstName }) => {
  const [screen, setScreen]         = useState('onboarding'); // 'onboarding' | 'quiz'
  const [difficulty, setDifficulty] = useState(null);
  const [mode, setMode]             = useState(null);

  const quizRef = useRef(null);
  const onboardingRef = useRef(null);

  const handleStart = (diff, m) => {
    setDifficulty(diff);
    setMode(m);
    setScreen('quiz');
    setTimeout(() => {
      if (quizRef.current) {
        quizRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };

  const handleBack = () => {
    setScreen('onboarding');
    setTimeout(() => {
      if (onboardingRef.current) {
        onboardingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };

  if (screen === 'onboarding') return <OnboardingScreen onStart={handleStart} containerRef={onboardingRef} />;
  return <QuizScreen difficulty={difficulty} mode={mode} firstName={firstName} onBack={handleBack} containerRef={quizRef} />;
};

export default VerbQuiz;